import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

interface DistributorApisStatus {
  digikeyConfigured: boolean;
  mouserConfigured: boolean;
  mouserSearchConfigured?: boolean;
  mouserOrderConfigured?: boolean;
  digikeyOrdering?: {
    sandbox: {
      clientConfigured: boolean;
      connected: boolean;
      expiresAt: string | null;
      productionEnabled: boolean;
    };
    production: {
      clientConfigured: boolean;
      connected: boolean;
      expiresAt: string | null;
      productionEnabled: boolean;
    };
  };
  envFilePath: string;
  envFile: {
    hasDigikeyClientId: boolean;
    hasDigikeyClientSecret: boolean;
    hasDigikeySandboxClientId?: boolean;
    hasDigikeySandboxClientSecret?: boolean;
    hasMouserApiKey: boolean;
    hasMouserSearchApiKey?: boolean;
    hasMouserOrderApiKey?: boolean;
  };
  environment: {
    hasDigikeyClientId: boolean;
    hasDigikeyClientSecret: boolean;
    hasDigikeySandboxClientId?: boolean;
    hasDigikeySandboxClientSecret?: boolean;
    hasMouserApiKey: boolean;
    hasMouserSearchApiKey?: boolean;
    hasMouserOrderApiKey?: boolean;
  };
}

interface DistributorPartSnapshot {
  name: "DigiKey" | "Mouser";
  mpn: string;
  manufacturer: string | null;
  description: string | null;
  stock: number;
  leadDays: number | null;
  prices: { qty: number; price: number }[];
  inStock: boolean;
  productUrl: string | null;
  datasheetUrl: string | null;
  lifecycle: string | null;
}

interface DistributorBranch {
  attempt: "skipped" | "ok";
  notConfigured?: boolean;
  part: DistributorPartSnapshot | null;
}

interface DistributorApisTestResult {
  mpn: string;
  digikey: DistributorBranch;
  mouserSearch?: DistributorBranch;
  mouser: DistributorBranch;
  mouserCart?: {
    attempt: "skipped" | "ok";
    notConfigured?: boolean;
    cart: { cartKey: string | null; itemCount: number; currencyCode: string | null } | null;
    error: string | null;
  };
}

function formatTestPayload(testResult: DistributorApisTestResult | null): string {
  if (testResult == null) {
    return "";
  }
  return JSON.stringify(testResult, null, 2);
}

export function DistributorApis() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const hubDistributorSettingsAllowed = useHubModuleAdmin("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const distributorSettingsAdminTitle =
    "Module administrator access on the Admin hub module is required to save distributor API credentials to disk.";
  const [mpn, setMpn] = useState("");
  const [digikeyClientId, setDigikeyClientId] = useState("");
  const [digikeyClientSecret, setDigikeyClientSecret] = useState("");
  const [mouserApiKey, setMouserApiKey] = useState("");
  const [mouserOrderApiKey, setMouserOrderApiKey] = useState("");
  const [testResult, setTestResult] = useState<DistributorApisTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const [digikeyPanelOpen, setDigikeyPanelOpen] = useState(false);
  const [mouserPanelOpen, setMouserPanelOpen] = useState(false);
  const [digikeySaveId, setDigikeySaveId] = useState("");
  const [digikeySaveSecret, setDigikeySaveSecret] = useState("");
  const [digikeySandboxSaveId, setDigikeySandboxSaveId] = useState("");
  const [digikeySandboxSaveSecret, setDigikeySandboxSaveSecret] = useState("");
  const [mouserSaveKey, setMouserSaveKey] = useState("");
  const [mouserOrderSaveKey, setMouserOrderSaveKey] = useState("");
  const [mouserOrderPanelOpen, setMouserOrderPanelOpen] = useState(false);
  const [saveFormError, setSaveFormError] = useState<string | null>(null);
  const [saveSuccessNote, setSaveSuccessNote] = useState<string | null>(null);

  const statusQuery = useQuery({
    queryKey: ["user-admin", "distributor-apis", "status"],
    queryFn: () =>
      apiFetch<DistributorApisStatus>("/api/user-admin/distributor-apis/status"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, string | null>) =>
      apiFetch<{ ok: boolean }>("/api/user-admin/distributor-apis/settings", {
        method: "PUT",
        body,
      }),
    onSuccess: (_data, body) => {
      setSaveFormError(null);
      if (
        "digikeyClientId" in body &&
        "digikeyClientSecret" in body &&
        body.digikeyClientId === null &&
        body.digikeyClientSecret === null
      ) {
        setSaveSuccessNote("Stored DigiKey lines were removed from the .env file.");
        setDigikeyPanelOpen(false);
      } else if ("mouserApiKey" in body && body.mouserApiKey === null) {
        setSaveSuccessNote("Stored Mouser Search line was removed from the .env file.");
        setMouserPanelOpen(false);
      } else if ("mouserOrderApiKey" in body && body.mouserOrderApiKey === null) {
        setSaveSuccessNote("Stored Mouser Order/Cart line was removed from the .env file.");
        setMouserOrderPanelOpen(false);
      } else {
        setSaveSuccessNote("Saved to the .env file on the API server. Values are not shown again in this form.");
        if (typeof body.digikeyClientId === "string") {
          setDigikeySaveId("");
        }
        if (typeof body.digikeyClientSecret === "string") {
          setDigikeySaveSecret("");
        }
        if (typeof body.mouserApiKey === "string") {
          setMouserSaveKey("");
        }
        if (typeof body.mouserOrderApiKey === "string") {
          setMouserOrderSaveKey("");
        }
      }
      window.setTimeout(() => {
        setSaveSuccessNote(null);
      }, 5000);
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "distributor-apis", "status"] });
    },
    onError: (err) => {
      setSaveFormError(err instanceof Error ? err.message : "Save failed.");
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      setTestError(null);
      setTestResult(null);
      const body: Record<string, string> = { mpn: mpn.trim() };
      if (digikeyClientId.trim() !== "" || digikeyClientSecret.trim() !== "") {
        body.digikeyClientId = digikeyClientId;
        body.digikeyClientSecret = digikeyClientSecret;
      }
      if (mouserApiKey.trim() !== "") {
        body.mouserApiKey = mouserApiKey;
      }
      if (mouserOrderApiKey.trim() !== "") {
        body.mouserOrderApiKey = mouserOrderApiKey;
      }
      try {
        const data = await apiFetch<{ result: DistributorApisTestResult }>(
          "/api/user-admin/distributor-apis/test",
          { method: "POST", body },
        );
        return data.result;
      } catch (err) {
        if (err instanceof ApiError && err.status === 400 && err.body && typeof err.body === "object") {
          const payload = err.body as { error?: string; result?: DistributorApisTestResult };
          if (payload.result) {
            setTestError(payload.error ?? "Request failed");
            return payload.result;
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      setTestResult(data);
    },
  });

  const digikeyOAuthMutation = useMutation({
    mutationFn: (environment: "sandbox" | "production") =>
      apiFetch<{ authorizeUrl: string }>(
        "/api/user-admin/distributor-apis/digikey/oauth/start",
        {
          method: "POST",
          body: {
            environment,
            returnTo: "/admin/distributor-apis",
            uiOrigin: window.location.origin,
          },
        },
      ),
    onSuccess: ({ authorizeUrl }) => {
      window.location.assign(authorizeUrl);
    },
    onError: (error) => {
      setSaveFormError(error instanceof Error ? error.message : "Could not start DigiKey sign-in.");
    },
  });

  const status = statusQuery.data;
  const running = testMutation.isPending;
  const runningSave = saveMutation.isPending;
  const fileFlags = status?.envFile;
  const env = status?.environment;
  const envFilePath = status?.envFilePath;

  function onSaveDigikey(): void {
    setSaveFormError(null);
    const body: Record<string, string> = {};
    if (digikeySaveId.trim() !== "") {
      body.digikeyClientId = digikeySaveId.trim();
    }
    if (digikeySaveSecret.trim() !== "") {
      body.digikeyClientSecret = digikeySaveSecret.trim();
    }
    if (digikeySandboxSaveId.trim() !== "") {
      body.digikeySandboxClientId = digikeySandboxSaveId.trim();
    }
    if (digikeySandboxSaveSecret.trim() !== "") {
      body.digikeySandboxClientSecret = digikeySandboxSaveSecret.trim();
    }
    if (Object.keys(body).length === 0) {
      setSaveFormError("Enter a client id and client secret, or at least one new value to update.");
      return;
    }
    saveMutation.mutate(body);
  }

  function onSaveMouserSearch(): void {
    setSaveFormError(null);
    if (mouserSaveKey.trim() === "") {
      setSaveFormError("Enter a Mouser Search API key to save.");
      return;
    }
    saveMutation.mutate({ mouserApiKey: mouserSaveKey.trim() });
  }

  function onSaveMouserOrder(): void {
    setSaveFormError(null);
    if (mouserOrderSaveKey.trim() === "") {
      setSaveFormError("Enter a Mouser Order/Cart API key to save.");
      return;
    }
    saveMutation.mutate({ mouserOrderApiKey: mouserOrderSaveKey.trim() });
  }

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="DigiKey and Mouser use separate credentials. Mouser Search (MOUSER_API_KEY) is for part lookup and Shopping discovery. Mouser Order/Cart (MOUSER_ORDER_API_KEY) is the key from Manage APIs for Order, Cart, and Order History — not the Search enrollment. Saving writes into the API .env file (see Credentials in use). Test search uses the Search key; test also calls Cart GET when the Order key is set.">
          Distributor APIs
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Credentials in use</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4 text-sm text-slate-700">
          {statusQuery.isLoading ? (
            <p>Loading…</p>
          ) : statusQuery.isError ? (
            <p className="text-red-700">Could not load status.</p>
          ) : status ? (
            <>
              {envFilePath ? (
                <p className="text-xs break-all text-slate-500">
                  <span className="font-medium text-slate-600">.env file on server: </span>
                  {envFilePath}
                </p>
              ) : null}
              {saveFormError ? (
                <p className="text-sm text-red-700" role="alert">
                  {saveFormError}
                </p>
              ) : null}
              {saveSuccessNote ? (
                <p className="text-sm text-green-800" role="status">
                  {saveSuccessNote}
                </p>
              ) : null}

              <div className="space-y-1 border-b border-slate-200 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    DigiKey production application: search and ordering{" "}
                    <span className="font-semibold text-slate-900">
                      {status.digikeyConfigured ? "ready" : "not ready"}
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setDigikeyPanelOpen((o) => !o);
                      setSaveFormError(null);
                    }}
                  >
                    {digikeyPanelOpen ? "Close" : "Replace keys"}
                  </Button>
                </div>
                {fileFlags ? (
                  <p className="text-xs text-slate-500">
                    In .env file: client id {fileFlags.hasDigikeyClientId ? "set" : "not set"},
                    client secret {fileFlags.hasDigikeyClientSecret ? "set" : "not set"}.
                    {env ? (
                      <>
                        {" "}
                        In process environment: id {env.hasDigikeyClientId ? "set" : "not set"},
                        secret {env.hasDigikeyClientSecret ? "set" : "not set"}.
                      </>
                    ) : null}
                  </p>
                ) : null}
                {status.digikeyOrdering ? (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {(["sandbox", "production"] as const).map((environment) => {
                      const ordering = status.digikeyOrdering![environment];
                      return (
                        <div key={environment} className="rounded border border-slate-200 p-2">
                          <p className="font-medium capitalize">{environment} Ordering API</p>
                          <p className="text-xs text-slate-600">
                            Application {ordering.clientConfigured ? "configured" : "not configured"};
                            OAuth {ordering.connected ? " connected" : " not connected"}.
                          </p>
                          {environment === "production" && !ordering.productionEnabled ? (
                            <p className="mt-1 text-xs text-amber-700">
                              Production submission is locked until coordinated DigiKey testing is enabled.
                            </p>
                          ) : null}
                          <ReadOnlyHoverLayer
                            active={!hubDistributorSettingsAllowed}
                            hoverHint={distributorSettingsAdminTitle}
                          >
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="mt-2"
                              disabled={
                                !hubDistributorSettingsAllowed ||
                                !ordering.clientConfigured ||
                                digikeyOAuthMutation.isPending
                              }
                              onClick={() => digikeyOAuthMutation.mutate(environment)}
                            >
                              {ordering.connected ? "Reconnect" : "Connect"}
                            </Button>
                          </ReadOnlyHoverLayer>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {digikeyPanelOpen ? (
                  <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs text-slate-600">
                      New values are written into the .env file. Previous values are not shown. Leave
                      a field empty if you only want to change the other.
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor="digikey-save-id">DigiKey client id</Label>
                        <Input
                          id="digikey-save-id"
                          value={digikeySaveId}
                          onChange={(e) => setDigikeySaveId(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="digikey-save-secret">DigiKey client secret</Label>
                        <Input
                          id="digikey-save-secret"
                          type="password"
                          value={digikeySaveSecret}
                          onChange={(e) => setDigikeySaveSecret(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                    <p className="text-xs font-medium text-slate-700">
                      Sandbox application credentials
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor="digikey-sandbox-save-id">Sandbox client id</Label>
                        <Input
                          id="digikey-sandbox-save-id"
                          value={digikeySandboxSaveId}
                          onChange={(event) => setDigikeySandboxSaveId(event.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="digikey-sandbox-save-secret">Sandbox client secret</Label>
                        <Input
                          id="digikey-sandbox-save-secret"
                          type="password"
                          value={digikeySandboxSaveSecret}
                          onChange={(event) => setDigikeySandboxSaveSecret(event.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                      <Button
                        type="button"
                        onClick={onSaveDigikey}
                        disabled={runningSave || !hubDistributorSettingsAllowed}
                        size="sm"
                      >
                        {runningSave ? "Saving…" : "Save"}
                      </Button>
                      </ReadOnlyHoverLayer>
                      {fileFlags?.hasDigikeyClientId || fileFlags?.hasDigikeyClientSecret ? (
                        <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setSaveFormError(null);
                            saveMutation.mutate({
                              digikeyClientId: null,
                              digikeyClientSecret: null,
                            });
                          }}
                          disabled={runningSave || !hubDistributorSettingsAllowed}
                        >
                          Remove DigiKey lines from .env
                        </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    Mouser Search API (<code className="text-xs">MOUSER_API_KEY</code>):{" "}
                    <span className="font-semibold text-slate-900">
                      {status.mouserSearchConfigured ?? status.mouserConfigured ? "ready" : "not ready"}
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setMouserPanelOpen((o) => !o);
                      setSaveFormError(null);
                    }}
                  >
                    {mouserPanelOpen ? "Close" : "Replace key"}
                  </Button>
                </div>
                {fileFlags ? (
                  <p className="text-xs text-slate-500">
                    In .env file: {fileFlags.hasMouserApiKey ? "key set" : "not set"}.
                    {env ? <> In process environment: {env.hasMouserApiKey ? "set" : "not set"}.</> : null}
                  </p>
                ) : null}
                {mouserPanelOpen ? (
                  <div className="mt-3 max-w-md space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs text-slate-600">
                      Search API enrollment (part data, Shopping discover). Not the Order/Cart key from Manage
                      APIs.
                    </p>
                    <div className="space-y-1">
                      <Label htmlFor="mouser-save-key">Mouser Search API key</Label>
                      <Input
                        id="mouser-save-key"
                        type="password"
                        value={mouserSaveKey}
                        onChange={(e) => setMouserSaveKey(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                      <Button
                        type="button"
                        onClick={onSaveMouserSearch}
                        disabled={runningSave || !hubDistributorSettingsAllowed}
                        size="sm"
                      >
                        {runningSave ? "Saving…" : "Save"}
                      </Button>
                      </ReadOnlyHoverLayer>
                      {fileFlags?.hasMouserApiKey ? (
                        <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setSaveFormError(null);
                            saveMutation.mutate({ mouserApiKey: null });
                          }}
                          disabled={runningSave || !hubDistributorSettingsAllowed}
                        >
                          Remove Search key from .env
                        </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="space-y-1 border-t border-slate-200 pt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    Mouser Order/Cart API (<code className="text-xs">MOUSER_ORDER_API_KEY</code>):{" "}
                    <span className="font-semibold text-slate-900">
                      {status.mouserOrderConfigured ? "ready" : "not ready"}
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setMouserOrderPanelOpen((o) => !o);
                      setSaveFormError(null);
                    }}
                  >
                    {mouserOrderPanelOpen ? "Close" : "Replace key"}
                  </Button>
                </div>
                {fileFlags ? (
                  <p className="text-xs text-slate-500">
                    In .env file: {fileFlags.hasMouserOrderApiKey ? "order key set" : "order key not set"}.
                    {env ? (
                      <> In process environment: {env.hasMouserOrderApiKey ? "set" : "not set"}.</>
                    ) : null}
                  </p>
                ) : null}
                {mouserOrderPanelOpen ? (
                  <div className="mt-3 max-w-md space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs text-slate-600">
                      UUID from Mouser Manage APIs (Order, Cart, Order History). Optional org-wide
                      fallback; each buyer can save their own key under Personal → Settings.
                    </p>
                    <div className="space-y-1">
                      <Label htmlFor="mouser-order-save-key">Mouser Order/Cart API key</Label>
                      <Input
                        id="mouser-order-save-key"
                        type="password"
                        value={mouserOrderSaveKey}
                        onChange={(e) => setMouserOrderSaveKey(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                        <Button
                          type="button"
                          onClick={onSaveMouserOrder}
                          disabled={runningSave || !hubDistributorSettingsAllowed}
                          size="sm"
                        >
                          {runningSave ? "Saving…" : "Save"}
                        </Button>
                      </ReadOnlyHoverLayer>
                      {fileFlags?.hasMouserOrderApiKey ? (
                        <ReadOnlyHoverLayer active={!hubDistributorSettingsAllowed} hoverHint={distributorSettingsAdminTitle}>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setSaveFormError(null);
                              saveMutation.mutate({ mouserOrderApiKey: null });
                            }}
                            disabled={runningSave || !hubDistributorSettingsAllowed}
                          >
                            Remove Order key from .env
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test search by manufacturer part number</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">
            One-off DigiKey or Mouser values here are used only for this test request and are not
            written to the .env file.
          </p>
          <div className="space-y-2">
            <Label htmlFor="distributor-mpn">Manufacturer part number (MPN)</Label>
            <Input
              id="distributor-mpn"
              value={mpn}
              onChange={(e) => setMpn(e.target.value)}
              placeholder="e.g. GRM32ER71E226KE15L"
              autoComplete="off"
            />
          </div>

          <p className="text-sm text-slate-600">
            Optional: override for this test only (empty uses saved or environment values):
          </p>

          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="digikey-client-id">DigiKey client id (test only)</Label>
              <Input
                id="digikey-client-id"
                value={digikeyClientId}
                onChange={(e) => setDigikeyClientId(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="digikey-client-secret">DigiKey client secret (test only)</Label>
              <Input
                id="digikey-client-secret"
                type="password"
                value={digikeyClientSecret}
                onChange={(e) => setDigikeyClientSecret(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            <div className="max-w-md space-y-2">
              <Label htmlFor="mouser-api-key">Mouser Search API key (test only)</Label>
              <Input
                id="mouser-api-key"
                type="password"
                value={mouserApiKey}
                onChange={(e) => setMouserApiKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div className="max-w-md space-y-2">
              <Label htmlFor="mouser-order-api-key">Mouser Order/Cart API key (test only)</Label>
              <Input
                id="mouser-order-api-key"
                type="password"
                value={mouserOrderApiKey}
                onChange={(e) => setMouserOrderApiKey(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          {testError ? (
            <p className="text-sm text-amber-800" role="alert">
              {testError}
            </p>
          ) : null}
          {testMutation.isError && !(testError && testResult) ? (
            <p className="text-sm text-red-700" role="alert">
              {testMutation.error instanceof Error
                ? testMutation.error.message
                : "Test failed."}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle}>
            <Button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={running || mpn.trim() === "" || !hubWritesAllowed}
            >
              {running ? "Running…" : "Run test"}
            </Button>
            </ReadOnlyHoverLayer>
          </div>

          {testResult ? (
            <div>
              <p className="mb-1 text-sm font-medium text-slate-800">Result (JSON)</p>
              <pre className="max-h-96 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
                {formatTestPayload(testResult)}
              </pre>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
